/**
 * SetPasswordModal
 * Shown once after onboarding activation for hosts who completed signup
 * via phone-OTP (and therefore never set a password).
 */
import React, { useState } from 'react';
import { Modal, Form, Input, Button, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setPassword } from '../../../redux/authentication/actionCreator';
import HttpStatus from '../../../utility/status.enum';

const SetPasswordModal = ({ onClose }) => {
  const [form] = Form.useForm();
  const [apiError, setApiError] = useState('');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(false);
  const { passwordPattern } = global;

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      setApiError('');

      const res = await dispatch(setPassword({
        password:              values.password,
        password_confirmation: values.password_confirmation,
      }));

      if (res?.status === HttpStatus.HTTP_OK) {
        localStorage.removeItem('onboarding_needs_password');
        onClose();
        navigate('/settings', { state: { activeTab: 10 } });
      } else {
        setApiError(res?.message);
      }
    } catch (err) {
      if (err?.errorFields) return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open
      title="Set your password"
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      wrapClassName="hg-set-password-modal"
    >
      <div className='project-modal'>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
        >
          <Form.Item
            label="New password"
            name="password"
            rules={[
              {
                required: true,
                message: 'Please enter your password',
              },
              {
                min: 8,
                message: 'Password must be at least 8 characters long',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || value.length < 8) {
                    return Promise.resolve();
                  }
                  const pattern = new RegExp(passwordPattern);
                  if (pattern.test(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error('Password must include uppercase, lowercase, number, and special character'),
                  );
                },
              }),
            ]}
          >
            <Input.Password placeholder="Enter your new password" autoComplete="new-password" tabIndex="1" />
          </Form.Item>

          <Form.Item
            label="Confirm password"
            name="password_confirmation"
            dependencies={['password']}
            rules={[
              { required: true, message: 'Please confirm your password.' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match.'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="Enter confirm password" autoComplete="new-password" tabIndex="2" />
          </Form.Item>

          {apiError && (
            <span className='setPassword_modal_error'>
              {apiError}
            </span>
          )}

          <div>
            <Button type="primary" htmlType="submit" loading={loading}>
              Save password
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default SetPasswordModal;
